import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ElasticsearchService } from '@netfoundry-ui/shared/elasticsearch';
import { TEMPLATE_SEARCH_SERVICE, TemplateSearchService } from '@netfoundry-ui/shared/apiv2';
import { ApiService, LoggerService } from '@netfoundry-ui/shared/services';
import { parseTrafficFlows } from '@netfoundry-ui/shared/service-templates';
import type { TrafficRow } from '@netfoundry-ui/shared/model';
import {
    buildTrafficSankey,
    flowsForLink,
    flowsForNode,
    highlightForFlows,
    type SankeyChartLink,
    type SankeyChartNode,
    type SankeyLinkClickEvent,
    type SankeyNodeClickEvent,
    type SankeyNodeToggleEvent,
    type TrafficSankeyData,
} from '@netfoundry-ui/ui/flow-map-chart';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import {
    buildCoverageGraph,
    buildSelectionFlows,
    coverageCounts,
    coverageGraphFromFlows,
    explainFlows,
    flowPairKey,
    isolateSelected,
    segmentStates,
    setEndpointIncluded,
    setSourceIncluded,
    type CoverageCounts,
    type CoverageExplain,
    type CoverageGraph,
    type CoverageRowBy,
    type CoverageServiceInput,
    type CoverageState,
    type SelectionPair,
} from './coverage-sankey.adapter';
import type { CoverageIncludeToggle } from './coverage-detail-content.component';

/** Everything the dialog needs: a snapshot of the service cards, the selection, and its time window. */
export interface CoverageDialogData {
    services: CoverageServiceInput[];
    pairs: SelectionPair[];
    /** Comma-joined dest addresses / ports, for the restricted ("Selected only") re-query. */
    destIp: string;
    destPort: string;
    srcIdentities: string[];
    srcIps: string[];
    hostIdentities: string[];
    /** The originating time window (epoch ms); absent on a deep link without it. */
    start?: number;
    end?: number;
    label?: string;
    coveredCount: number;
    totalSegments: number;
}

type Mode = 'selected' | 'full';
/** The coverage-state filter pills above the graph ('all' shows every state). */
type Filter = 'all' | CoverageState;

/**
 * Modal that shows the Traffic Analysis flow-map (Sankey) colored by how much of the selected traffic the
 * in-progress service cards cover. Opens on the selected segments only (a cheap restricted re-query) and
 * can expand to the full window's traffic with everything outside the selection faded back. If the window
 * is missing or the re-query fails it degrades to a graph drawn from the selection alone.
 */
@Component({
    selector: 'app-coverage-sankey-dialog',
    templateUrl: './coverage-sankey-dialog.component.html',
    styleUrls: ['./coverage-sankey-dialog.component.scss'],
    standalone: false,
})
export class CoverageSankeyDialogComponent implements OnInit {
    mode: Mode = 'selected';
    loading = false;
    error = '';
    /** Whether the full-graph toggle is offered (needs the originating window and a working re-query). */
    hasWindow = false;
    /** True once we have fallen back to the from-selection graph (no real magnitudes, no context). */
    degraded = false;

    nodes: readonly SankeyChartNode[] = [];
    links: readonly SankeyChartLink[] = [];
    headers: readonly string[] = [];
    highlightNodeIds: readonly string[] = [];
    highlightLinkIds: readonly string[] = [];
    /** Accurate, source-aware coverage of the selected traffic (by circuits), from the fetched flows. */
    private coveredCircuits = 0;
    private selectedCircuits = 0;

    /** Active coverage-state filter pill, and the per-state segment counts shown on the pills. */
    filter: Filter = 'all';
    counts: CoverageCounts = { covered: 0, partial: 0, uncovered: 0, total: 0 };

    /** The clicked element's coverage readout, shown in the side drawer; null when nothing is selected. */
    detail: { eyebrow: string; title: string; explain: CoverageExplain } | null = null;
    /** The open panel's source flows + context, so an include/exclude edit can recompute and reopen it. */
    private detailCtx: { eyebrow: string; title: string; flows: TrafficRow[]; rowsBy: CoverageRowBy } | null = null;
    /** Set once the viewer edits a service here, so the edits are returned to the page on close. */
    private servicesDirty = false;
    /** Source identities currently drilled into (their source-address column is shown). Keyed by the
     *  identity's node value (the part after the `1:` stage prefix). */
    private readonly expandedIdentities = new Set<string>();

    private readonly flowCache = new Map<Mode, TrafficRow[]>();
    /** The raw Sankey index (flows per node/ribbon) for the currently drawn graph, for hover/click. */
    private displayData: TrafficSankeyData = {
        nodes: [],
        links: [],
        headers: [],
        flowsByNode: new Map(),
        flowsByLink: new Map(),
        labelById: new Map(),
    };
    /** The mode's resting highlight, re-applied when a hover ends. */
    private baseHighlightNodes: readonly string[] = [];
    private baseHighlightLinks: readonly string[] = [];
    /** The endpoint column index in the shared Sankey (source address 0, identities 1-2, endpoint 3). */
    private readonly STAGE_ENDPOINT = 3;

    constructor(
        public dialogRef: MatDialogRef<CoverageSankeyDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CoverageDialogData,
        private elasticsearch: ElasticsearchService,
        @Inject(TEMPLATE_SEARCH_SERVICE) private templateService: TemplateSearchService,
        private apiService: ApiService,
        private logger: LoggerService
    ) {}

    ngOnInit(): void {
        this.hasWindow = !!(this.data.start && this.data.end);
        this.load('selected');
    }

    get windowLabel(): string {
        if (!this.data.start || !this.data.end) return '';
        const fmt = (t: number) => moment(t).format('MMM D, HH:mm');
        return `${fmt(this.data.start)} – ${fmt(this.data.end)}`;
    }

    /** Source-aware coverage of the selected traffic by circuits, or null until flows have loaded. */
    get trafficPercent(): number | null {
        return this.selectedCircuits > 0 ? Math.round((this.coveredCircuits / this.selectedCircuits) * 100) : null;
    }

    /** True when there is nothing to draw (query returned no selected flows), once loading has settled. */
    get isEmpty(): boolean {
        return !this.loading && this.nodes.length === 0;
    }

    setMode(mode: Mode): void {
        if (mode === this.mode || this.loading) return;
        this.mode = mode;
        this.load(mode);
    }

    /** Narrow the graph to one coverage state. Flows are already cached, so this just re-filters. */
    setFilter(filter: Filter): void {
        if (filter === this.filter || this.loading) return;
        this.filter = filter;
        this.rebuild();
    }

    close(): void {
        // Hand the edited service snapshot back to the page so it can apply the include/exclude changes.
        this.dialogRef.close(this.servicesDirty ? this.data.services : undefined);
    }

    private async load(mode: Mode): Promise<void> {
        this.loading = true;
        this.error = '';
        try {
            await this.flowsFor(mode); // fetch + cache the flows for this scope
            this.rebuild();
        } catch (error) {
            this.logger?.error?.(error);
            if (mode === 'full') {
                // Keep the selected view intact; just report that the context graph could not load.
                this.mode = 'selected';
                this.error = 'The full graph could not be loaded. Showing the selected segments.';
                this.rebuild();
            } else {
                this.degradeToSelection();
            }
        } finally {
            this.loading = false;
        }
    }

    /**
     * Rebuild from the cached flows for the current scope. Counts and the headline coverage come from all
     * selected flows; the drawn graph is narrowed to the active filter pill's state.
     */
    private rebuild(): void {
        const flows = this.flowCache.get(this.mode);
        if (!flows) return;
        const states = segmentStates(flows, this.data.pairs, this.data.services);
        this.counts = coverageCounts(states);
        // The drawn graph is narrowed to the active filter pill's state; its raw Sankey index is kept so a
        // hover/click resolves to its flows. Ribbons render proportionally (green covered + neutral
        // excluded bands sized by circuits); partial *nodes* read amber-hatched.
        const displayFlows =
            this.filter === 'all' ? flows : flows.filter((f) => states.get(flowPairKey(f)) === this.filter);
        this.displayData = buildTrafficSankey(displayFlows, { expandedIdentities: this.expandedIdentities });
        const graph = buildCoverageGraph(this.displayData, this.data.pairs, this.data.services, 'proportional');
        // The headline coverage is over ALL selected flows, not the filtered subset.
        const full =
            this.filter === 'all' ? graph : coverageGraphFromFlows(flows, this.data.pairs, this.data.services);
        this.coveredCircuits = full.coveredCircuits;
        this.selectedCircuits = full.selectedCircuits;
        // A mode/filter change invalidates whatever element was open.
        this.clearDetail();
        this.apply(this.mode, graph);
    }

    private apply(mode: Mode, graph: CoverageGraph): void {
        if (mode === 'selected') {
            const iso = isolateSelected(graph);
            this.nodes = iso.nodes;
            this.links = iso.links;
            this.headers = iso.headers;
            this.baseHighlightNodes = [];
            this.baseHighlightLinks = [];
        } else {
            // Full graph: feed everything and highlight the selection so the rest fades.
            this.nodes = graph.nodes;
            this.links = graph.links;
            this.headers = graph.headers;
            this.baseHighlightNodes = graph.selectedNodeIds;
            this.baseHighlightLinks = graph.selectedLinkIds;
        }
        this.restoreHighlight();
    }

    // --- hover: light the hovered element's path, fade the rest ---------------

    onNodeHover(ev: SankeyNodeClickEvent | null): void {
        if (!ev) {
            this.restoreHighlight();
            return;
        }
        this.applyHoverHighlight(this.displayData.flowsByNode.get(ev.nodeId) ?? []);
    }

    private applyHoverHighlight(flows: TrafficRow[]): void {
        const { nodeIds, linkIds } = highlightForFlows(this.displayData, flows);
        this.highlightNodeIds = nodeIds;
        // highlightForFlows returns base ribbon ids; the drawn ribbons are split (base#cov/#unc/#ctx), so
        // map each base id onto the split ids actually present.
        const baseSet = new Set(linkIds);
        this.highlightLinkIds = this.links.filter((l) => baseSet.has(this.baseLinkId(l.id))).map((l) => l.id);
    }

    private restoreHighlight(): void {
        this.highlightNodeIds = this.baseHighlightNodes;
        this.highlightLinkIds = this.baseHighlightLinks;
    }

    // --- click: open the coverage detail drawer ------------------------------

    onNodeClick(ev: SankeyNodeClickEvent): void {
        const resolved = flowsForNode(this.displayData, ev);
        // Always break down by source: excluding a source is the only edit scoped to one path's traffic, so
        // every element (node or ribbon) is characterized by the sources carrying its flows.
        this.openDetail(resolved.eyebrow, resolved.title, resolved.flows, 'source');
    }

    onLinkClick(ev: SankeyLinkClickEvent): void {
        // Resolve against the base ribbon id (the split suffix is only for drawing).
        const resolved = flowsForLink(this.displayData, { ...ev, linkId: this.baseLinkId(ev.linkId) });
        this.openDetail(resolved.eyebrow, resolved.title, resolved.flows, 'source');
    }

    onBackground(): void {
        this.restoreHighlight();
        this.clearDetail();
    }

    /** Drill a source identity in (show its source addresses) or collapse it back, then recolor the graph. */
    onNodeToggle(ev: SankeyNodeToggleEvent): void {
        const value = ev.nodeId.slice(ev.nodeId.indexOf(':') + 1);
        if (ev.expanded) {
            this.expandedIdentities.add(value);
        } else {
            this.expandedIdentities.delete(value);
        }
        this.rebuild();
    }

    /** A breakdown row in the detail drawer is hovered: light that row's path in the flow-map; restore on leave. */
    onDetailRowHover(ev: { flows: TrafficRow[] } | null): void {
        if (!ev) {
            this.restoreHighlight();
            return;
        }
        this.applyHoverHighlight(ev.flows);
    }

    /** A source's include checkbox was toggled: edit the governing service(s), then recompute the graph and
     *  reopen the same panel so the meter + states reflect the change. The edits are returned on close. */
    onDetailIncludeToggle(ev: CoverageIncludeToggle): void {
        const ctx = this.detailCtx;
        if (!ctx) return;
        // A parent cascades over several items; apply each. Source rows edit admission (allowed sources);
        // endpoint rows edit the service's targets, using the whole graph's flows as the "still needed" set.
        const contextFlows = this.flowCache.get(this.mode) ?? ctx.flows;
        let changed = false;
        for (const item of ev.items) {
            if (ev.target === 'source') {
                // contextFlows (the whole graph) is the "keep everyone else" set when materializing an
                // allow-all service, so excluding this segment's source does not drop the other sources.
                if (setSourceIncluded(ctx.flows, item, ev.include, this.data.services, contextFlows)) changed = true;
            } else {
                const idx = item.lastIndexOf(':');
                const address = idx >= 0 ? item.slice(0, idx) : item;
                const port = idx >= 0 ? item.slice(idx + 1) : '';
                if (setEndpointIncluded(address, port, ev.include, this.data.services, contextFlows)) changed = true;
            }
        }
        if (!changed) return;
        this.servicesDirty = true;
        this.rebuild(); // recolors the graph + recomputes the headline against the edited services
        this.openDetail(ctx.eyebrow, ctx.title, ctx.flows, ctx.rowsBy); // refresh the open panel
    }

    closeDetail(): void {
        this.clearDetail();
    }

    private openDetail(eyebrow: string, title: string, flows: TrafficRow[], rowsBy: CoverageRowBy): void {
        const explain = explainFlows(flows, this.data.pairs, this.data.services, rowsBy);
        this.detail = { eyebrow, title, explain };
        this.detailCtx = { eyebrow, title, flows, rowsBy };
    }

    private clearDetail(): void {
        this.detail = null;
        this.detailCtx = null;
    }

    /** Strip the proportional-ribbon suffix (`<base>#cov`) back to the base ribbon id. */
    private baseLinkId(id: string): string {
        const hash = id.indexOf('#');
        return hash >= 0 ? id.slice(0, hash) : id;
    }

    private async flowsFor(mode: Mode): Promise<TrafficRow[]> {
        const cached = this.flowCache.get(mode);
        if (cached) return cached;
        if (!this.hasWindow) throw new Error('no-window');
        const network = this.apiService.currentNetwork?.value;
        if (!network?.id || !network?.networkGroupId) throw new Error('no-network');
        const start = moment(this.data.start).utc().format();
        const end = moment(this.data.end).utc().format();
        // Selected mode restricts to the selection's dest IPs/ports (small result); full mode queries the
        // whole window and never restricts the source side.
        const restrictIp = mode === 'selected' ? this.jsonArray(this.data.destIp) : '';
        const restrictPort = mode === 'selected' ? this.jsonArray(this.data.destPort) : '';
        const params = this.templateService.getTrafficAnalysisData(
            'ncfabriccircuits',
            start,
            end,
            undefined,
            undefined,
            network.id,
            network.networkGroupId,
            restrictIp,
            restrictPort,
            ''
        );
        const resp = await firstValueFrom(
            this.elasticsearch.apiTemplateSearch(this.templateService.TRAFFIC_ANALYSIS_DATA_TEMPLATE, params)
        );
        const flows = parseTrafficFlows(resp);
        this.flowCache.set(mode, flows);
        return flows;
    }

    private degradeToSelection(): void {
        this.degraded = true;
        this.hasWindow = false; // no full-graph context without the window
        this.mode = 'selected';
        const flows = buildSelectionFlows(
            this.data.pairs,
            this.data.srcIdentities,
            this.data.srcIps,
            this.data.hostIdentities
        );
        this.flowCache.set('selected', flows); // so the ribbon-style toggle still works offline
        this.rebuild();
        this.error = flows.length ? '' : 'There are no selected segments to show.';
    }

    /** Format a comma-joined list as the template's quoted JSON array; empty list -> no restriction. */
    private jsonArray(csv: string): string {
        const out = (csv || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => `"${s}"`);
        return out.length ? `[${out.join(',')}]` : '';
    }
}
